import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTable, useRowSelect, useExpanded, useSortBy } from "react-table";
import Loader from "./Loader";
import "./AppTable.css";
import TableRow from "./TableRow";
import OrderOverviewStatusTooltip from "./OrderOverviewStatusTooltip";
import PendingOrderProgressToolTip from "./PendingOrderProgressToolTip";
import CheckboxIntermediate from "../../components/global/CheckboxIntermediate/CheckboxIntermediate";
import PastPaymentsInvoiceDetails from "../orderinvoices/PastPaymentsInvoiceDetails";
import { useDispatch, useSelector } from "react-redux";
import { lawFirmSelector } from "../../features/lawFirm/lawFirmSlice";
import { authLoginSelector } from "../../features/auth/authLoginSlice";
import { useTranslation } from "react-i18next";
import {
  GetCCtransactionInvoices,
  GetChequeTransactionInvoices,
  GetEFTTransactionInvoices,
  GetPreAuthorizedTransactionInvoices,
} from "../../features/invoice/invoiceSlice";
import OrderOverviewResultsTooltip from "./OrderOverviewResultsTooltip";

const AppTable = ({
  columns,
  data,
  isLoading,
  manualPagination = false,
  manualSorting = false,
  onSort,
  pickupInvoice,
  isPastPayments,
  invoiceInsideColumns,
  setSelectedRows,
  isHeaderChecked,
  isCurrentFirmPromptFinancial,
}) => {
  const { userLogin } = useSelector(authLoginSelector);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [sortColumn, setSortColumn] = useState(null);
  const [sortOrder, setSortOrder] = useState("ascending");
  const columnData = useMemo(
    () => columns(userLogin, t, isCurrentFirmPromptFinancial),
    [columns, userLogin, t, isCurrentFirmPromptFinancial]
  );
  const rowData = useMemo(() => data, [data]);
  const { loading } = useSelector(lawFirmSelector);
  const [sublevelData, setSublevelData] = useState([]);
  const [sublevelInvoiceLoading, setSublevelInvoiceLoading] = useState(null);
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onSort(sortColumn, sortOrder);
  }, [sortColumn, sortOrder]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    selectedFlatRows,
  } = useTable(
    {
      columns: columnData,
      data: rowData,
      manualPagination,
      manualSorting,
      initialState: {
        // sortBy: sortees,
        hiddenColumns: [
          "missingInfos",
          "OverDueDays",
          "ccCardType",
          "ccMaskNumber",
          "invoiceDetails",
          "invoiceDetails.invoiceLineItems",
          "invoiceLineItems",
          "invoiceTax",
          "invoiceSubTotal",
          "paymentType",
          "quoteId",
          "cardNumber"
        ],
      },
    },
    // useSortBy,
    useExpanded,
    useRowSelect,
    (hooks) => {
      if (pickupInvoice || isPastPayments) {
        hooks.visibleColumns.push((columns) => [
          {
            width: 30,
            sortable: false,
            id: "selection",
            Header: ({ getToggleAllRowsSelectedProps }) => (
              <CheckboxIntermediate
                {...getToggleAllRowsSelectedProps()}
                isHeaderChecked={isHeaderChecked}
              />
            ),
            Cell: ({ row }) => (
              <CheckboxIntermediate
                {...row.getToggleRowSelectedProps()}
                name={row.original.invoiceID}
              />
            ),
          },
          ...columns,
        ]);
      }
      if (isPastPayments) {
        hooks.visibleColumns.push((columns) => [
          {
            width: 10,
            sortable: false,
            // id: "selection",
            Cell: ({ row }) => (
              <button
                {...row.getToggleRowExpandedProps({
                  style: {
                    paddingLeft: `${row.depth * 2}rem`,
                  },

                  onClick: async (e) => {
                    e.stopPropagation();
                    row.toggleRowExpanded();

                    const paymentID = row?.original?.paymentID;
                    if (!row.isExpanded && !sublevelData[paymentID]) {
                      setSublevelInvoiceLoading((prev) => ({
                        ...prev,
                        [paymentID]: true,
                      }));
                      const invoiceDetailsData = await dispatch(
                        row?.original?.paymentMethod?.includes("Credit Card")
                          ? GetCCtransactionInvoices(
                              userLogin?.userId,
                              paymentID
                            )
                          : row?.original?.paymentMethod?.includes("Cheque")
                          ? GetChequeTransactionInvoices(
                              userLogin?.userId,
                              paymentID
                            )
                          : row?.original?.paymentMethod?.includes("EFT")
                          ? GetEFTTransactionInvoices(
                              userLogin?.userId,
                              paymentID
                            )
                          : GetPreAuthorizedTransactionInvoices(
                              userLogin?.userId,
                              paymentID
                            )
                      );

                      if (invoiceDetailsData) {
                        setSublevelData((prev) => ({
                          ...prev,
                          [paymentID]: invoiceDetailsData,
                        }));
                        setSublevelInvoiceLoading((prev) => ({
                          ...prev,
                          [paymentID]: false,
                        }));
                      } else {
                        console.error("Failed to fetch sublevel data");
                        setSublevelInvoiceLoading((prev) => ({
                          ...prev,
                          [paymentID]: false,
                        }));
                      }
                    }
                  },
                })}
              >
                {" "}
                {row.isExpanded ? "-" : "+"}{" "}
              </button>
            ),
          },
          ...columns,
        ]);
      }
    }
  );

  useEffect(() => {
    if (selectedFlatRows) {
      if (setSelectedRows) setSelectedRows(selectedFlatRows);
    }
  }, [selectedFlatRows?.length]);
  const handleSort = (column) => {
    let newSortOrder = "ascending";

    if (sortColumn === column) {
      // If the same column is clicked again, reverse the sort order
      if (sortOrder === "descending" && column === sortColumn) {
        setSortColumn(column);
      } else {
        newSortOrder = sortOrder === "ascending" ? "descending" : "ascending";
        setSortColumn(column);
      }
    } else {
      setSortColumn(column);
    }
    setSortOrder(newSortOrder);
  };

  
  const getPastPaymentsSubRowColumns=(row)=>{
    if(row?.original?.paymentMethod.includes("Credit Card"))
      return invoiceInsideColumns.creditCardSubColumns;
    if(row?.original?.paymentMethod.includes("EFT"))
      return invoiceInsideColumns.EFTSubColumns;
    if(row?.original?.paymentMethod.includes("Cheque"))
      return invoiceInsideColumns.CheuqueSubColumns;
    if(row?.original?.paymentMethod.includes("PreAuth"))
      return invoiceInsideColumns.PreAuthSubColumns;
  }


  return (
    <>
      <table
        {...getTableProps()}
        className="!w-[1340px] border border-[#A09F9F]"
      >
        <thead className="bg-[#FFF6DF] border-[#A09F9F] border-b ">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => {
                return (
                  <th
                    className="pb-[16px] pt-[24px] text-left font-[600] text-[14px] pr-4 pl-4"
                    // {...column.getHeaderProps(column.getSortByToggleProps())}
                    {...column.getHeaderProps()}
                    onClick={() => {
                      if (column.sortable) {
                        handleSort(column?.id);
                      }
                    }}
                    style={{ width: column.width, cursor: "pointer" }}
                  >
                    {column?.isVisible && column.render("Header")}
                    {column?.ToolTip &&
                      (column?.Header === "Status" ||
                        column?.Header === "Statut") && (
                        <OrderOverviewStatusTooltip />
                      )}
                      {column?.ToolTip &&
                      (column?.Header === "Results" ||
                        column?.Header === "Résultats") && (
                        <OrderOverviewResultsTooltip />
                      )}
                    {column?.ToolTip && column?.Header === "Progress" && (
                      <PendingOrderProgressToolTip />
                    )}
                    {sortColumn === column?.id && (
                      <span>{sortOrder === "ascending" ? "▼" : "▲"}</span>
                    )}
                    {/* <span>
                      {column.isSorted ? (column.isSortedDesc ? "▲" : "▼") : ""}
                    </span> */}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {rows?.map((row, i) => {
            prepareRow(row);
            return (
              <>
                <tr
                  {...row.getRowProps()}
                  className="!h-fit font-[400] hover:bg-[#F8F8F8] pl-[32px] pr-[32px] text-[14px] "
                >
                  {row.cells.map((cell, index) => {
                    return <TableRow cell={cell} index={index} />;
                  })}
                </tr>
                {row?.isExpanded ? (
                  <tr>
                    <td colSpan={8}>
                      <PastPaymentsInvoiceDetails
                        columns={getPastPaymentsSubRowColumns(row)}
                        data={sublevelData[row.original.paymentID] || []}
                        sublevelInvoiceLoading={
                          sublevelInvoiceLoading[row.original.paymentID]
                        }
                      />
                    </td>{" "}
                  </tr>
                ) : null}
              </>
            );
          })}
        </tbody>
      </table>
      {manualPagination && (
        <div>
          {/* YOUR CLIENT SIDE PAGINATION COMPONENT BUILT USING REACT-TABLE UTILITY FUNCTIONS */}
        </div>
      )}
      {isLoading || loading ? <Loader /> : ""}
    </>
  );
};

export default AppTable;
